import React, { useState, useEffect } from "react";
import { Eye, AlertCircle, Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { formatDateOnlyForDisplay, isFechaVencidaColombia } from "../../../../../../utils/dateUtils";

const PreManifiestoList = ({ mantisBusinessId, mantisBusinessUserId, token, onPreManifiestoSelect, onCreatePreManifiesto, refreshTrigger }) => {
  const [preManifiestos, setPreManifiestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingPreManifiestoId, setDeletingPreManifiestoId] = useState(null);
  const [filters, setFilters] = useState({ estado: null });

  useEffect(() => {
    loadPreManifiestos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mantisBusinessId, refreshTrigger, filters]);

  const loadPreManifiestos = async () => {
    setLoading(true);
    try {
      const res = await roldanApi.getPreManifiestos(mantisBusinessId, token, { limit: 100, ...filters });
      if (res?.success) setPreManifiestos(res.data || []);
    } catch (error) {
      console.error("Error loading pre-manifiestos:", error);
    } finally {
      setLoading(false);
    }
  };

  const chipCls = (estado) => {
    const e = (estado || '').toLowerCase();
    if (e.includes('abierto')) return 'bg-sky-100 text-sky-800';
    if (e.includes('cerrado')) return 'bg-gray-100 text-gray-700';
    if (e.includes('manifestado')) return 'bg-violet-100 text-violet-800';
    return 'bg-gray-100 text-gray-700';
  };

  const hasNoRemesas = (pm) => Number(pm.cantidad_remesas || 0) === 0;

  const handleDeletePreManifiesto = async (pm, e) => {
    e?.stopPropagation?.();
    if (!hasNoRemesas(pm)) return;
    if (!window.confirm(`¿Eliminar el pre-manifiesto ${pm.numero_pre_manifiesto}? No tiene remesas vinculadas.`)) return;
    setDeletingPreManifiestoId(pm.pre_manifiesto_id);
    try {
      const res = await roldanApi.deletePreManifiesto(mantisBusinessId, token, pm.pre_manifiesto_id);
      if (res?.success) {
        toast.success("Pre-manifiesto eliminado");
        loadPreManifiestos();
      } else {
        toast.error(res?.message || "Error al eliminar el pre-manifiesto");
      }
    } catch (err) {
      console.error("Error deleting pre-manifiesto:", err);
      toast.error("Error al eliminar el pre-manifiesto");
    } finally {
      setDeletingPreManifiestoId(null);
    }
  };

  return (
    <div className="px-2">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 p-4 border-b border-gray-100">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Pre-Manifiestos</h1>
            <p className="text-sm text-gray-500">Agrupaciones de remesas por viaje</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <select
              value={filters.estado || ""}
              onChange={(e) => setFilters({ ...filters, estado: e.target.value || null })}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white w-full sm:w-auto"
            >
              <option value="">Todos los estados</option>
              <option value="Abierto">Abierto</option>
              <option value="Cerrado">Cerrado</option>
              <option value="Manifestado">Manifestado</option>
            </select>
            {onCreatePreManifiesto && (
              <button 
                onClick={onCreatePreManifiesto} 
                className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg w-full sm:w-auto"
              >
                <Plus size={16} /> Nuevo Pre-Manifiesto
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : (
          <div className="grid gap-3 p-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {preManifiestos.map((pm) => {
              const vencido = isFechaVencidaColombia(pm.fecha_viaje);
              const isManifestado = (pm.estado || '').toLowerCase().includes('manifestado');
              const showVencido = vencido && !isManifestado;
              return (
              <div key={pm.pre_manifiesto_id} className={`bg-gradient-to-b from-white to-gray-50 border rounded-xl p-4 shadow-sm hover:shadow-md transition ${showVencido ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-extrabold text-gray-900">{pm.numero_pre_manifiesto}</span>
                    {showVencido && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-xs bg-red-500 text-white">
                        <AlertCircle size={12} /> Vencido
                      </span>
                    )}
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full font-semibold text-xs ${chipCls(pm.estado)}`}>{pm.estado}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                  <p className={showVencido ? 'text-red-700 font-semibold' : ''}>
                    <span className="text-gray-500 font-semibold">Fecha Viaje:</span> {formatDateOnlyForDisplay(pm.fecha_viaje)}
                  </p>
                  <p><span className="text-gray-500 font-semibold">Remesas:</span> {pm.cantidad_remesas || 0}</p>
                  {pm.origen && (
                    <p><span className="text-gray-500 font-semibold">Origen:</span> {pm.origen}</p>
                  )}
                  {pm.destino && (
                    <p><span className="text-gray-500 font-semibold">Destino:</span> {pm.destino}</p>
                  )}
                </div>
                <div className="pt-3 mt-2 border-t border-dashed border-gray-200 flex items-center justify-end gap-2">
                  {hasNoRemesas(pm) && (
                    <button
                      type="button"
                      onClick={(e) => handleDeletePreManifiesto(pm, e)}
                      disabled={deletingPreManifiestoId === pm.pre_manifiesto_id}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                      title="Eliminar pre-manifiesto (sin remesas)"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  <button onClick={() => onPreManifiestoSelect(pm.pre_manifiesto_id)} className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg">
                    <Eye size={16} /> Ver Detalle
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PreManifiestoList;

